import { apiFetch } from "./client";

/**
 * D-055 S4 — Working Queue API (visual v0.2 екран 1).
 * GET /Custom/frontend/meetings — resolver-gated, backend-owned counts.
 */

export type MeetingsQueueFilter =
  | "today"
  | "planned"
  | "report_required"
  | "overdue"
  | "conducted"
  | "cancelled";

export type MeetingsQueueScope = "own" | "team";

export interface MeetingsQueueItem {
  id: string;
  summary: string;
  meetingType: string | null;
  meetingStatus: string;
  occurredAt: string | null;
  durationMinutes: number | null;
  contactName: string | null;
  accountName: string | null;
  targetHoldingId: string | null;
  /** D-134 — лід-зустріч (target = prospect-Account). Badge «Потенційний клієнт». */
  isProspect?: boolean;
  reportSlaDueAt: string | null;
  reportOverdueSince: string | null;
  reportSubmitted: boolean;
  reportRequired: boolean;
  reviewStatus: "not_required" | "pending_review" | "accepted" | "returned";
  assignedUserName: string | null;
  /** D-151 S2 (P73) — CAS-токен. */
  interactionRevision: number;
  /** D-151 S2 (C8) — chip «заповнив керівник». */
  reportSubmissionMode: "own" | "supervisor_on_behalf" | "admin_override" | null;
  reportSubmittedByName: string | null;
  allowedActions: {
    canFillReport: boolean;
    canMarkOccurred: boolean;
    canFillReportOnBehalf: boolean;
    canFillReportOnBehalfBlockedByFreshDraft: boolean;
    canMarkOccurredOnBehalf: boolean;
  };
}

export interface MeetingsQueueResponse {
  items: MeetingsQueueItem[];
  total: number;
  /** Backend-owned counts усіх 6 вкладок (frontend НЕ інферить). */
  counts: Record<MeetingsQueueFilter, number>;
  scope: MeetingsQueueScope;
  filter: MeetingsQueueFilter;
  allowedScopes: MeetingsQueueScope[];
  /** S5 — видимість вкладки «Контроль команди» (backend tier-gate). */
  controlAllowed: boolean;
}

export function fetchMeetingsQueue(
  params: { scope?: MeetingsQueueScope; filter?: MeetingsQueueFilter },
  signal?: AbortSignal,
): Promise<MeetingsQueueResponse> {
  const q = new URLSearchParams();
  if (params.scope) q.set("scope", params.scope);
  if (params.filter) q.set("filter", params.filter);
  const qs = q.toString();

  return apiFetch<MeetingsQueueResponse>(
    `/Custom/frontend/meetings${qs ? `?${qs}` : ""}`,
    { method: "GET", signal },
  );
}

export interface MarkOccurredResponse {
  status: string;
  interactionId: string;
  meetingOccurred: string;
  meetingStatus: string;
}

/**
 * «Проведено» з черги → 3.1A mark-occurred (occurred=yes). Рядок мігрує у
 * «Звіт очікується» (доктрина: проведена без звіту = незавершена робота);
 * backend емітить meeting_report_required (D-043).
 */
export function markMeetingOccurred(
  id: string,
  interactionRevision: number,
): Promise<MarkOccurredResponse> {
  return apiFetch<MarkOccurredResponse>(
    `/Custom/interactions/${encodeURIComponent(id)}/mark-occurred`,
    { method: "POST", body: { occurred: "yes", interactionRevision } },
  );
}

/**
 * D-151 S2 (Q-I1=B) — «Проведено» ЗА менеджера: supervisor + reason (10–500),
 * server-гейт canMarkOccurredOnBehalf; менеджер отримує in_app подію (P58).
 */
export function markMeetingOccurredOnBehalf(
  id: string,
  body: { reason: string; interactionRevision: number },
): Promise<MarkOccurredResponse> {
  return apiFetch<MarkOccurredResponse>(
    `/Custom/interactions/${encodeURIComponent(id)}/mark-occurred`,
    { method: "POST", body: { occurred: "yes", onBehalf: true, ...body } },
  );
}
